import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import { blogPosts, getBlogBySlug, getRelatedPosts } from '@/mocks/blogData';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);

  const post = slug ? getBlogBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug) : [];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-firmai-dark flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-2xl font-bold mb-4">文章未找到</h1>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all"
          >
            返回博客列表
          </button>
        </div>
      </div>
    );
  }

  // Parse markdown-like content to HTML
  const renderContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-white font-semibold text-xl md:text-2xl mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-white font-semibold text-lg mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('- ')) {
        const listItems: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('- ')) {
          listItems.push(lines[i].trim().replace('- ', ''));
          i++;
        }
        i--;
        elements.push(
          <ul key={key++} className="space-y-2 my-4">
            {listItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-firmai-text text-sm md:text-base leading-relaxed">
                <span className="w-1.5 h-1.5 bg-firmai-green rounded-full shrink-0 mt-2" />
                {item}
              </li>
            ))}
          </ul>
        );
      } else if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ') || line.startsWith('4. ') || line.startsWith('5. ') || line.startsWith('6. ') || line.startsWith('7. ') || line.startsWith('8. ') || line.startsWith('9. ') || line.startsWith('10. ')) {
        const listItems: string[] = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
          listItems.push(lines[i].trim().replace(/^\d+\.\s/, ''));
          i++;
        }
        i--;
        elements.push(
          <ol key={key++} className="space-y-2 my-4 list-decimal list-inside">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-firmai-text text-sm md:text-base leading-relaxed pl-1">
                {item}
              </li>
            ))}
          </ol>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={key++} className="text-white font-semibold text-sm md:text-base my-4">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else {
        elements.push(
          <p key={key++} className="text-firmai-text text-sm md:text-base leading-relaxed my-4">
            {line}
          </p>
        );
      }
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-firmai-dark" ref={sectionRef}>
      <Navbar />

      <main>
        {/* Hero Image */}
        <section className="relative pt-20">
          <div className="relative h-72 md:h-96 overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-firmai-dark via-firmai-dark/60 to-transparent" />
          </div>
        </section>

        {/* Article Content */}
        <section className="relative -mt-32 z-10">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              {/* Meta */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-firmai-green/20 text-firmai-green text-xs font-medium rounded-full border border-firmai-green/30">
                  {post.categoryLabel}
                </span>
                <span className="text-firmai-muted text-sm">{post.date}</span>
                <span className="text-firmai-muted text-sm">{post.readTime}阅读</span>
              </div>

              {/* Title */}
              <h1 className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 font-display font-bold text-2xl md:text-4xl text-white leading-tight">
                {post.title}
              </h1>

              {/* Author */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-150 flex items-center gap-3 mt-6 pb-8 border-b border-firmai-border">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="text-white font-medium">{post.author.name}</div>
                  <div className="text-firmai-muted text-sm">{post.author.title}</div>
                </div>
              </div>

              {/* Content */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 py-8">
                {renderContent(post.content)}
              </div>

              {/* Tags */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-wrap gap-2 pt-6 border-t border-firmai-border">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-firmai-dark border border-firmai-border rounded-full text-firmai-text text-xs hover:border-firmai-green/30 hover:text-firmai-green transition-all duration-200 cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex items-center gap-3 mt-6 pt-6 border-t border-firmai-border">
                <span className="text-firmai-muted text-sm">分享文章：</span>
                <button className="w-9 h-9 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-muted hover:text-firmai-green hover:border-firmai-green/30 transition-all duration-200">
                  <i className="ri-wechat-fill" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-muted hover:text-firmai-green hover:border-firmai-green/30 transition-all duration-200">
                  <i className="ri-linkedin-fill" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-muted hover:text-firmai-green hover:border-firmai-green/30 transition-all duration-200">
                  <i className="ri-weibo-fill" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-muted hover:text-firmai-green hover:border-firmai-green/30 transition-all duration-200">
                  <i className="ri-link" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="relative py-16 md:py-24 bg-firmai-darker">
            <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
              <div className="max-w-3xl mx-auto">
                <h2 className="reveal opacity-0 translate-y-6 transition-all duration-700 font-display font-bold text-xl md:text-2xl text-white mb-8">
                  相关文章
                </h2>
                <div className="space-y-4">
                  {relatedPosts.map((related, index) => (
                    <div
                      key={related.id}
                      className="reveal opacity-0 translate-y-6 transition-all duration-700 flex items-start gap-4 bg-firmai-card border border-firmai-border rounded-xl p-4 hover:border-firmai-green/30 transition-all duration-300 cursor-pointer group"
                      style={{ transitionDelay: `${index * 100}ms` }}
                      onClick={() => navigate(`/blog/${related.slug}`)}
                    >
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-24 h-16 md:w-32 md:h-20 rounded-lg object-cover shrink-0 group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-firmai-green text-xs">{related.categoryLabel}</span>
                          <span className="text-firmai-muted text-xs">{related.date}</span>
                        </div>
                        <h3 className="text-white text-sm md:text-base font-medium line-clamp-2 group-hover:text-firmai-green transition-colors duration-200">
                          {related.title}
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.06)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">想要了解更多？</h2>
              <p className="text-firmai-text mt-4 text-base md:text-lg">
                订阅我们的行业洞察，或预约一对一专家咨询
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => navigate('/blog')}
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-firmai-green hover:text-firmai-green transition-all duration-200 whitespace-nowrap group"
                >
                  浏览更多文章
                  <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                    <i className="ri-arrow-right-line" />
                  </span>
                </button>
                <a
                  href="/#demo"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                    setTimeout(() => {
                      document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  预约专家咨询
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}